import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarDays, Clock, ArrowRight, UserRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = {
  gruppo_presenza: "Gruppo in presenza",
  gruppo_online: "Gruppo online",
  individuale: "Percorso individuale",
};
const SUBS = {
  gruppo_presenza: "Mercoledì · 17:00 – 18:30 · Via Generale Guisan 8B, Lugano Paradiso",
  gruppo_online: "Giovedì · 20:00 – 22:00 · in videochiamata",
  individuale: "6 incontri da 75 minuti · in presenza o online",
};

const PercorsoModal = ({ percorso, posti, onClose, onChoose }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (percorso) {
      axios.get(`${API}/calendario`).then(({ data }) => setItems(data.items || [])).catch(() => {});
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [percorso]);

  const isGroup = percorso === "gruppo_presenza" || percorso === "gruppo_online";
  const modKey = percorso === "gruppo_online" ? "online" : "presenza";
  const cicli = isGroup
    ? [1, 2].map((n) => ({
        n,
        key: `${modKey}_${n}`,
        label: n === 1 ? "Primo ciclo · Gennaio – Giugno 2027" : "Secondo ciclo · Luglio – Dicembre 2027",
        short: n === 1 ? "Primo ciclo" : "Secondo ciclo",
        rows: items
          .filter(
            (i) =>
              (modKey === "online" ? i.modalita === "online" : i.modalita !== "online") &&
              i.anno === 2027 &&
              (n === 1 ? i.numero <= 6 : i.numero > 6)
          )
          .sort((a, b) => a.numero - b.numero),
      }))
    : [];

  return (
    <AnimatePresence>
      {percorso && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-[#0C1B33]/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="percorso-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl border border-[#E2D9CD] bg-[#FFFDF9] p-6 shadow-[0_40px_90px_rgba(12,27,51,0.4)] sm:rounded-3xl sm:p-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="percorso-modal"
          >
            <button
              data-testid="percorso-modal-close"
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full border border-[#E2D9CD] bg-[#FAF8F5] p-2 text-[#0C1B33] transition-colors hover:border-[#C93B2B] hover:text-[#C93B2B]"
              aria-label="Chiudi"
            >
              <X className="h-4 w-4" />
            </button>

            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0D9488]">
              Le date proposte
            </p>
            <h3 className="font-display text-3xl font-semibold text-[#0C1B33]" data-testid="percorso-modal-title">
              {TITLES[percorso]}
            </h3>
            <p className="mt-1.5 text-sm text-[#475569]">{SUBS[percorso]}</p>

            {percorso === "individuale" ? (
              <div className="mt-7 rounded-2xl border border-[#E2D9CD] bg-[#FAF8F5] p-6" data-testid="percorso-modal-individuale">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0C1B33]/5 text-[#0C1B33]">
                    <UserRound className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm leading-relaxed text-[#334155]">
                      Il percorso individuale non ha date fisse: <strong>6 incontri da 75 minuti</strong> che
                      concordiamo insieme in base alle tue disponibilità, in presenza a Lugano Paradiso oppure
                      online. Si inizia con la psicoeducazione sui 6 temi, poi decidi tu se proseguire.
                    </p>
                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.14em] text-[#0D9488]">
                      Iscrizione sempre aperta
                    </p>
                  </div>
                </div>
                <button
                  data-testid="percorso-modal-iscriviti-individuale"
                  onClick={() => onChoose("individuale", null)}
                  className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0C1B33] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0D9488]"
                >
                  Iscriviti al percorso individuale
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            ) : (
              <div className="mt-7 grid grid-cols-1 gap-6 md:grid-cols-2">
                {cicli.map((ciclo) => {
                  const info = posti?.cicli?.[ciclo.key];
                  const pieno = Boolean(info && info.rimasti <= 0);
                  return (
                    <div
                      key={ciclo.key}
                      className="flex flex-col rounded-2xl border border-[#E2D9CD] bg-[#FAF8F5] p-5"
                      data-testid={`percorso-modal-ciclo-${ciclo.n}`}
                    >
                      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#C93B2B]">
                        {ciclo.label}
                      </p>
                      {info && (
                        <p className={`mt-1 text-xs font-semibold ${pieno ? "text-[#C93B2B]" : "text-[#0D9488]"}`}>
                          {pieno ? "Posti esauriti — lista d'attesa" : `${info.rimasti} posti liberi su ${info.capienza}`}
                        </p>
                      )}
                      <ul className="mt-4 flex-1 space-y-2.5">
                        {ciclo.rows.map((r) => (
                          <li key={r.id} className="border-b border-dashed border-[#E2D9CD] pb-2.5 text-xs last:border-b-0">
                            <span className="block font-semibold text-[#0C1B33]">{r.titolo}</span>
                            <span className="mt-0.5 flex items-center gap-2 text-[#718096]">
                              <CalendarDays className="h-3 w-3 text-[#0D9488]" />
                              {r.data}
                              <Clock className="ml-1 h-3 w-3 text-[#0D9488]" />
                              {r.ora}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <button
                        data-testid={`percorso-modal-iscriviti-ciclo-${ciclo.n}`}
                        onClick={() => onChoose(percorso, `ciclo_${ciclo.n}`)}
                        className={`group mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                          pieno
                            ? "border border-amber-400/70 bg-amber-50 text-amber-800 hover:bg-amber-500 hover:text-white"
                            : "bg-[#0C1B33] text-white hover:bg-[#0D9488]"
                        }`}
                      >
                        {pieno ? `Lista d'attesa · ${ciclo.short}` : `Iscriviti · ${ciclo.short}`}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PercorsoModal;
